import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Film, Search, Shield, CheckCircle, X as XIcon, Users,
} from "lucide-react";

// Backend-valid consent statuses (faphouse_routes.ConsentStatus)
const STATUS_OPTIONS = [
  { v: "not_sent", label: "Not sent", hex: "#71717a" },
  { v: "form_sent", label: "Form sent", hex: "#f59e0b" },
  { v: "form_signed", label: "Signed", hex: "#06B6D4" },
  { v: "submitted", label: "Submitted", hex: "#14B8A6" },
  { v: "published", label: "Published", hex: "#22c55e" },
  { v: "rejected", label: "Rejected", hex: "#ef4444" },
];
const STATUS_HEX = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.v, s.hex]));

// How consent is being obtained (faphouse_routes.FormType)
const FORM_TYPES = [
  { v: "new_form", label: "New Form", hex: "#22c55e" },
  { v: "old_form", label: "Old Form", hex: "#f59e0b" },
  { v: "onlyfans_verified", label: "OnlyFans Verified", hex: "#00AFF0" },
  { v: "justforfans_verified", label: "JustForFans Verified", hex: "#FF6B6B" },
  { v: "faphouse_member", label: "Faphouse Member", hex: "#a855f7" },
  { v: "invite_via_faphouse", label: "Sent invite via Faphouse", hex: "#06B6D4" },
];
const FORM_HEX = Object.fromEntries(FORM_TYPES.map((f) => [f.v, f.hex]));
const FORM_LABEL = Object.fromEntries(FORM_TYPES.map((f) => [f.v, f.label]));

const muxThumb = (id) =>
  `https://image.mux.com/${id}/thumbnail.jpg?width=200&height=112&fit_mode=smartcrop&time=2`;
const keyOf = (videoId, crmId) => `${videoId}::${crmId}`;
const cStatus = (c) => (c && c.status) || "not_sent";
const cForm = (c) => (c && c.form_type) || "new_form";

export default function Consents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formFilter, setFormFilter] = useState(""); // "" = all forms
  const [selected, setSelected] = useState({});
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkForm, setBulkForm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/faphouse/");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load consents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const videos = useMemo(() => {
    const list = data?.videos || [];
    return list.filter((v) => {
      if (search && !v.title?.toLowerCase().includes(search.toLowerCase())) return false;
      const cast = v.cast || [];
      if (formFilter && !cast.some((c) => cForm(c.consent) === formFilter)) return false;
      if (statusFilter && !cast.some((c) => cStatus(c.consent) === statusFilter)) return false;
      return true;
    });
  }, [data, search, statusFilter, formFilter]);

  const summary = data?.summary || {};
  const byStatus = summary.by_status || {};

  // ── Selection helpers ────────────────────────────────
  const selectedKeys = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedCount = selectedKeys.length;

  const toggleOne = (videoId, crmId) => {
    const k = keyOf(videoId, crmId);
    setSelected((p) => ({ ...p, [k]: !p[k] }));
  };
  const toggleVideo = (video, on) => {
    setSelected((p) => {
      const next = { ...p };
      (video.cast || []).forEach((c) => { next[keyOf(video.video_id, c.crm_id)] = on; });
      return next;
    });
  };
  const clearSelection = () => setSelected({});

  const applyBulk = async () => {
    if (!bulkStatus && !bulkForm) { toast.error("Pick a status or a form type to apply"); return; }
    const pairs = selectedKeys.map((k) => {
      const [video_id, crm_id] = k.split("::");
      return { video_id, crm_id };
    });
    setBusy(true);
    try {
      const body = { pairs };
      if (bulkStatus) body.status = bulkStatus;
      if (bulkForm) body.form_type = bulkForm;
      const r = await api.post("/api/faphouse/consent/bulk", body);
      toast.success(`Updated ${r.data?.updated ?? pairs.length} record${(r.data?.updated ?? pairs.length) === 1 ? "" : "s"}`);
      clearSelection();
      setBulkStatus(""); setBulkForm("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  };

  const updateOne = async (videoId, crmId, patch) => {
    try {
      await api.post("/api/faphouse/consent", { video_id: videoId, crm_id: crmId, ...patch });
      toast.success("Consent updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <AppLayout title="Consents" overline="⚖️ LEGAL · MODEL RELEASE · CONSENT TRACKER"
      actions={
        <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-[12px]" data-testid="consents-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }>

      {/* Info banner */}
      <div className="mb-5 p-4 rounded-lg border" style={{ borderColor: "rgba(168,85,247,0.25)", background: "rgba(168,85,247,0.06)" }}>
        <div className="flex items-start gap-3">
          <Shield size={20} style={{ color: "#a855f7" }} className="mt-0.5" />
          <div className="text-sm">
            <div className="font-bold mb-1" style={{ color: "var(--gio-text)" }}>Consent & model-release tracking</div>
            <div className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
              Track every performer's release per video. Choose how consent was obtained (New Form, Old Form,
              OnlyFans Verified, JustForFans Verified, Faphouse Member, or Sent invite via Faphouse), advance the status, and select many at once for bulk updates.
            </div>
          </div>
        </div>
      </div>

      {/* Form-type tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="consent-form-tabs">
        <FormTab active={formFilter === ""} onClick={() => setFormFilter("")} label="All Forms" testid="form-tab-all" />
        {FORM_TYPES.map((f) => (
          <FormTab key={f.v} active={formFilter === f.v} onClick={() => setFormFilter(formFilter === f.v ? "" : f.v)}
            label={f.label} hex={f.hex} testid={`form-tab-${f.v}`} />
        ))}
      </div>

      {/* Status stat chips */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-5" data-testid="consent-stats">
        <KStat label="Videos" value={summary.videos_total || 0} hex="var(--gio-secondary)" />
        {STATUS_OPTIONS.map((s) => (
          <KStat key={s.v} label={s.label} value={byStatus[s.v] || 0} hex={s.hex} clickable
            active={statusFilter === s.v}
            onClick={() => setStatusFilter(statusFilter === s.v ? "" : s.v)} testid={`consent-stat-${s.v}`} />
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted-3)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by video title…"
            data-testid="consent-search" className="gio-input w-full pl-8" />
        </div>
        {(statusFilter || formFilter) && (
          <button onClick={() => { setStatusFilter(""); setFormFilter(""); }} data-testid="consent-clear-filter"
            className="text-[11px] underline text-zinc-500 hover:text-white">Clear filters</button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12" style={{ color: "var(--gio-muted-3)" }}>Loading consent records…</div>
      ) : (
        <div className="space-y-3 pb-24">
          {videos.map((v) => (
            <VideoConsentCard key={v.video_id} video={v} selected={selected} onToggleOne={toggleOne}
              onToggleVideo={toggleVideo} onUpdateOne={updateOne} />
          ))}
          {videos.length === 0 && (
            <div className="text-center py-12" style={{ color: "var(--gio-muted-3)" }} data-testid="consent-empty">
              No videos match your filters
            </div>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-[280px] z-40 border-t px-4 py-3"
          data-testid="consent-bulk-bar"
          style={{ background: "linear-gradient(180deg, var(--gio-surface-hi) 0%, var(--gio-surface) 100%)", borderColor: "var(--gio-border)", backdropFilter: "blur(8px)" }}>
          <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
            <span className="text-[13px] font-bold" style={{ color: "var(--gio-text)" }} data-testid="consent-bulk-count">
              {selectedCount} selected
            </span>
            <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} data-testid="consent-bulk-status"
              className="gio-input text-[12px] py-1.5">
              <option value="">Set status…</option>
              {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
            <select value={bulkForm} onChange={(e) => setBulkForm(e.target.value)} data-testid="consent-bulk-form"
              className="gio-input text-[12px] py-1.5">
              <option value="">Set form type…</option>
              {FORM_TYPES.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
            <button onClick={applyBulk} disabled={busy || (!bulkStatus && !bulkForm)} data-testid="consent-bulk-apply"
              className="btn-primary text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
              <CheckCircle size={13} /> {busy ? "Applying…" : "Apply to selected"}
            </button>
            <button onClick={clearSelection} data-testid="consent-bulk-clear"
              className="btn-ghost text-[12px] inline-flex items-center gap-1.5">
              <XIcon size={12} /> Clear
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function FormTab({ active, onClick, label, hex = "var(--gio-primary)", testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-3 py-1.5 text-xs font-medium rounded-full border transition"
      style={{
        borderColor: active ? hex : "var(--gio-border)",
        background: active ? `${hex}22` : "var(--gio-surface)",
        color: active ? hex : "var(--gio-muted-2)",
      }}>
      {label}
    </button>
  );
}

function KStat({ label, value, hex, clickable, active, onClick, testid }) {
  const Tag = clickable ? "button" : "div";
  return (
    <Tag onClick={clickable ? onClick : undefined} type={clickable ? "button" : undefined} data-testid={testid}
      className={`p-3 rounded-xl border text-left transition-all duration-200 ${clickable ? "cursor-pointer hover:-translate-y-1 hover:shadow-lg" : ""}`}
      style={{ 
        borderColor: active ? hex : "rgba(255,255,255,0.08)", 
        background: active ? `${hex}20` : "rgba(255,255,255,0.03)",
        boxShadow: active ? `0 0 20px ${hex}40` : "none"
      }}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 truncate" style={{ color: active ? hex : "var(--gio-muted-3)" }}>{label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: hex }}>{value}</div>
    </Tag>
  );
}

function VideoConsentCard({ video, selected, onToggleOne, onToggleVideo, onUpdateOne }) {
  const cast = video.cast || [];
  const allChecked = cast.length > 0 && cast.every((c) => selected[keyOf(video.video_id, c.crm_id)]);
  const thumb = video.mux_playback_id ? muxThumb(video.mux_playback_id) : null;

  return (
    <div className="p-4 rounded-lg border" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}
      data-testid={`consent-video-${video.video_id}`}>
      <div className="flex items-start gap-3">
        {thumb ? (
          <img src={thumb} alt={video.title} className="w-28 h-16 rounded object-cover shrink-0"
            data-testid={`consent-thumb-${video.video_id}`} />
        ) : (
          <div className="w-28 h-16 rounded flex items-center justify-center shrink-0" style={{ background: "var(--gio-surface-hi)" }}>
            <Film size={20} style={{ color: "var(--gio-muted-3)" }} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-bold truncate" style={{ color: "var(--gio-text)" }}>{video.title}</div>
            {cast.length > 0 && (
              <label className="inline-flex items-center gap-1.5 text-[11px] shrink-0 cursor-pointer" style={{ color: "var(--gio-muted-2)" }}>
                <input type="checkbox" checked={allChecked} onChange={(e) => onToggleVideo(video, e.target.checked)}
                  data-testid={`consent-video-selectall-${video.video_id}`} />
                Select all
              </label>
            )}
          </div>
          <div className="text-[11px] mt-0.5 inline-flex items-center gap-2" style={{ color: "var(--gio-muted-3)" }}>
            <Users size={11} /> {cast.length} cast member{cast.length !== 1 ? "s" : ""}
            {video.city && <span>· {video.city}</span>}
            {video.faphouse_status && video.faphouse_status !== "none" && (
              <span className="font-mono-ui px-1.5 py-0.5 rounded text-[10px]" style={{ background: "#f59e0b", color: "#000" }}>
                FAP: {video.faphouse_status}
              </span>
            )}
          </div>

          {cast.length === 0 ? (
            <div className="mt-3 text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
              No cast linked. Add cast from <a href="/catalog" className="underline" style={{ color: "var(--gio-secondary)" }}>/catalog</a>.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {cast.map((c) => {
                const k = keyOf(video.video_id, c.crm_id);
                const status = cStatus(c.consent);
                const form = cForm(c.consent);
                return (
                  <div key={c.crm_id} className="flex items-center gap-2 flex-wrap py-1.5 border-t"
                    style={{ borderColor: "var(--gio-border)" }} data-testid={`consent-cast-${video.video_id}-${c.crm_id}`}>
                    <input type="checkbox" checked={!!selected[k]} onChange={() => onToggleOne(video.video_id, c.crm_id)}
                      data-testid={`consent-check-${video.video_id}-${c.crm_id}`} />
                    {c.photo ? (
                      <img src={c.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-heading"
                        style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                        {(c.stage_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="w-32 min-w-0">
                      <div className="text-[13px] font-medium truncate" style={{ color: "var(--gio-text)" }}>{c.stage_name || "Unknown"}</div>
                      {c.consent?.updated_at && (
                        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
                          {new Date(c.consent.updated_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    <select value={status} onChange={(e) => onUpdateOne(video.video_id, c.crm_id, { status: e.target.value })}
                      data-testid={`consent-status-${video.video_id}-${c.crm_id}`}
                      className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-transparent outline-none"
                      style={{ borderColor: STATUS_HEX[status], color: STATUS_HEX[status] }}>
                      {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v} style={{ background: "#0d0d10" }}>{s.label}</option>)}
                    </select>

                    <select value={form} onChange={(e) => onUpdateOne(video.video_id, c.crm_id, { form_type: e.target.value })}
                      data-testid={`consent-form-${video.video_id}-${c.crm_id}`}
                      className="text-[11px] px-2 py-1 rounded border bg-transparent outline-none"
                      style={{ borderColor: FORM_HEX[form], color: FORM_HEX[form] }}
                      title="How consent is obtained">
                      {FORM_TYPES.map((f) => <option key={f.v} value={f.v} style={{ background: "#0d0d10" }}>{f.label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
